/**
 * StudyDocsPanel — the study's protocol/ICF documents, opened in a new tab via a
 * presigned URL (v1). Inline rendering is deferred: the CRM bucket has no browser
 * CORS, so an in-window viewer would need a bytes-proxy endpoint.
 */
import { ExternalLink, FileText, Loader2 } from 'lucide-react';
import { StudyDocument } from '../../types';

interface Props {
  documents: StudyDocument[];
  loading?: boolean;
  /** Document id currently being opened (spinner on its row). */
  openingId?: string | null;
  onOpen: (documentId: string) => void;
}

const DOC_LABEL: Record<string, string> = {
  protocol: 'Protocol',
  icf: 'Informed Consent',
  amendment: 'Amendment',
  other: 'Document',
};

export default function StudyDocsPanel({ documents, loading, openingId, onOpen }: Props) {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
      </div>
    );
  }
  if (!documents || documents.length === 0) {
    return (
      <p className="py-8 text-center text-sm text-gray-500 dark:text-gray-400">
        No protocol or consent documents uploaded for this study yet.
      </p>
    );
  }
  return (
    <div className="space-y-2">
      {documents.map((d) => {
        const ready = d.extraction_status === 'complete';
        return (
          <button
            key={d.id}
            type="button"
            onClick={() => onOpen(d.id)}
            disabled={openingId === d.id}
            className="flex w-full items-center gap-3 rounded-lg border border-gray-200 bg-white p-3 text-left transition-colors duration-150 hover:border-primary-400 hover:bg-primary-50/40 disabled:opacity-60 dark:border-gray-700 dark:bg-gray-800 dark:hover:border-primary-600"
          >
            <FileText className="h-5 w-5 shrink-0 text-primary-600 dark:text-primary-400" />
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">
                {DOC_LABEL[d.doc_type] || d.doc_type}
                {d.version_label ? ` · ${d.version_label}` : ''}
              </p>
              <p className="truncate text-xs text-gray-500 dark:text-gray-400">
                {d.filename}{d.page_count ? ` · ${d.page_count} pages` : ''}
                {!ready ? ` · ${d.extraction_status}` : ''}
              </p>
            </div>
            {openingId === d.id
              ? <Loader2 className="h-4 w-4 shrink-0 animate-spin text-gray-400" />
              : <ExternalLink className="h-4 w-4 shrink-0 text-gray-400" />}
          </button>
        );
      })}
    </div>
  );
}
